import { useI18n } from '@affine/i18n';
import type { ImageBlockModel } from '@blocksuite/affine/model';
import {
  AttachmentIcon,
  BlockIcon,
  CodeBlockIcon,
  ConnectorCIcon,
  DatabaseTableViewIcon,
  DividerIcon,
  EmbedWebIcon,
  FrameIcon,
  GroupIcon,
  ImageIcon,
  MindmapIcon,
  PageIcon,
  PenIcon,
  ShapeIcon,
  TableIcon,
  TextIcon,
} from '@blocksuite/icons/rc';
import type { BlockStdScope } from '@blocksuite/std';
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import * as styles from './style.css';

type TargetKind =
  | 'image'
  | 'note'
  | 'shape'
  | 'connector'
  | 'frame'
  | 'attachment'
  | 'database'
  | 'table'
  | 'drawing'
  | 'group'
  | 'mindmap'
  | 'text'
  | 'code'
  | 'embed'
  | 'divider'
  | 'block';

const KIND_ICONS: Record<TargetKind, ReactNode> = {
  image: <ImageIcon />,
  note: <PageIcon />,
  shape: <ShapeIcon />,
  connector: <ConnectorCIcon />,
  frame: <FrameIcon />,
  attachment: <AttachmentIcon />,
  database: <DatabaseTableViewIcon />,
  table: <TableIcon />,
  drawing: <PenIcon />,
  group: <GroupIcon />,
  mindmap: <MindmapIcon />,
  text: <TextIcon />,
  code: <CodeBlockIcon />,
  embed: <EmbedWebIcon />,
  divider: <DividerIcon />,
  block: <BlockIcon />,
};

/** Maps a block flavour or surface element type to its sidebar kind. */
function kindOf(flavourOrType: string): TargetKind {
  const name = flavourOrType.toLowerCase().replace(/^affine:/, '');
  if (name === 'image') return 'image';
  if (name === 'note') return 'note';
  if (name === 'shape') return 'shape';
  if (name === 'connector') return 'connector';
  if (name === 'frame') return 'frame';
  if (name === 'attachment') return 'attachment';
  if (name === 'database') return 'database';
  if (name === 'table') return 'table';
  if (name === 'brush' || name === 'highlighter') return 'drawing';
  if (name === 'group') return 'group';
  if (name === 'mindmap') return 'mindmap';
  if (
    name === 'text' ||
    name === 'edgeless-text' ||
    name === 'paragraph' ||
    name === 'list'
  )
    return 'text';
  if (name === 'code') return 'code';
  if (name === 'bookmark' || name.startsWith('embed')) return 'embed';
  if (name === 'divider') return 'divider';
  return 'block';
}

const TOKEN = /^<([^<>\s]+)>$/;

/**
 * A stored preview is either selected text or space-separated `<flavour>`
 * tokens (see comment-provider getPreviewFromSelections).
 */
function parsePreview(
  preview: string
): { text: string } | { kinds: TargetKind[] } {
  const parts = preview.trim().split(/\s+/);
  const kinds = parts.map(part => TOKEN.exec(part)?.[1]);
  if (parts.length === 0 || kinds.some(kind => !kind)) {
    return { text: preview.trim() };
  }
  return { kinds: kinds.map(kind => kindOf(kind as string)) };
}

function imageSourceIdOf(
  std: BlockStdScope | null,
  targetBlockIds: readonly string[]
): string | null {
  if (!std) return null;
  for (const id of targetBlockIds) {
    const model = std.store.getBlock(id)?.model;
    if (model?.flavour === 'affine:image') {
      const sourceId = (model as ImageBlockModel).props.sourceId;
      if (sourceId) return sourceId;
    }
  }
  return null;
}

function useImageThumbnail(
  std: BlockStdScope | null,
  sourceId: string | null
): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!std || !sourceId) return;
    let objectUrl: string | null = null;
    let disposed = false;
    std.store.blobSync
      .get(sourceId)
      .then(blob => {
        if (disposed || !blob) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
      setUrl(null);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [std, sourceId]);
  return url;
}

/**
 * Miniature of the commented content: image thumbnail, text excerpt or block
 * icon with a readable type name. Clicks bubble to the comment item, which
 * navigates to the block.
 */
export const CommentTargetPreview = ({
  preview,
  targetBlockIds,
  std,
}: {
  preview: string;
  targetBlockIds: readonly string[];
  std: BlockStdScope | null;
}) => {
  const t = useI18n();
  const parsed = useMemo(() => parsePreview(preview), [preview]);
  const kinds = 'kinds' in parsed ? parsed.kinds : null;
  const sourceId = useMemo(
    () =>
      kinds?.includes('image') ? imageSourceIdOf(std, targetBlockIds) : null,
    [kinds, std, targetBlockIds]
  );
  const thumbnail = useImageThumbnail(std, sourceId);

  if ('text' in parsed) {
    return (
      <div
        className={styles.targetPreview}
        data-variant="text"
        title={t['com.streamlain.comment.target.jump']()}
      >
        <span className={styles.targetText}>{parsed.text}</span>
      </div>
    );
  }

  const kind = thumbnail ? 'image' : parsed.kinds[0];
  const extra = parsed.kinds.length - 1;
  return (
    <div
      className={styles.targetPreview}
      data-variant="block"
      title={t['com.streamlain.comment.target.jump']()}
    >
      {thumbnail ? (
        <img className={styles.targetThumbnail} src={thumbnail} alt="" />
      ) : (
        <span className={styles.targetIcon}>{KIND_ICONS[kind]}</span>
      )}
      <span className={styles.targetLabel}>
        {t[`com.streamlain.comment.target.${kind}`]()}
      </span>
      {extra > 0 ? <span className={styles.targetMore}>+{extra}</span> : null}
    </div>
  );
};
